import { Check, XIcon } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { BadgeStatus } from "@/components/dashboard_campaigns/status-badges";
import { StatusValue } from "@/types/types";
import { useAuth } from "@clerk/react";
import { useCreatorStatusStore, useCampaignStore, useCreatorsStore, useEveActionsStore } from "@/stores";
import { isEveCampaign } from "@/lib/eve";
import { useCreatorsTableContextOptional } from "@/components/creators-table/CreatorsTableContext";
import { ConfirmPopover, ConfirmPopoverLink } from "@/components/ui/confirm-popover";
import { Link } from "react-router-dom";
import posthog from "posthog-js";

interface WaitingApprovalActionProps {
  status: StatusValue;
  _id: string;
  isLoading: boolean;
  loadingThisCreator: string | null;
}

export default function WaitingApprovalAction({
  status,
  _id,
  isLoading,
  loadingThisCreator,
}: WaitingApprovalActionProps) {
  const context = useCreatorsTableContextOptional();
  const { getToken } = useAuth();
  const { updateCreatorStatus } = useCreatorStatusStore();
  const { rejectCreator } = useEveActionsStore();
  // Inside a table the row's own campaign decides (the org-wide Global CRM
  // mixes campaigns); elsewhere (Tasks) the open campaign in the store.
  const storeCampaign = useCampaignStore((s) => s.campaign);
  const creator = useCreatorsStore((s) => s.creators.find((c) => c._id === _id));
  const campaign = context ? context.campaignFor(creator) : storeCampaign;
  const campaignId = campaign?._id ?? null;
  const use_dub = campaign?.use_dub ?? false;
  const isEve = isEveCampaign(campaign);

  // If no context available, just show the badge without action buttons
  if (!context) {
    return (
      <div className="flex items-center gap-1">
        <BadgeStatus status={status} />
      </div>
    );
  }

  const { setApprovalDialogCreatorId, setLoadingStatusCreators, onRefresh } = context;

  const handleRejectCreator = async () => {
    posthog.capture("creator_status_update", {
      creatorId: _id,
      newStatus: "pending-rejection",
      campaignId,
    });

    setLoadingStatusCreators((prev) => ({
      ...prev,
      [_id]: "pending-rejection",
    }));

    try {
      const token = await getToken();
      if (token) {
        if (isEve) {
          // Eve campaigns: the amy service records the rejection and sends
          // the rejection email — never the legacy endpoint.
          await rejectCreator(_id, token);
        } else {
          await updateCreatorStatus(
            _id,
            "pending-rejection" as StatusValue,
            "",
            "",
            "",
            token,
            use_dub
          );
        }
      }
    } catch (error) {
      console.error("Error updating creator status:", error);
    } finally {
      setLoadingStatusCreators((prev) => {
        const updated = { ...prev };
        delete updated[_id];
        return updated;
      });
      onRefresh();
    }
  };

  return (
    <div className="flex items-center gap-1 transition-all duration-200 ease-out w-auto group:">
      <BadgeStatus status={status} />
      <div className="flex gap-2 ml-2 transition-all duration-200 ease-out">
        <Button
          size="icon"
          variant="success-ghost"
          className="shrink-0 size-7.5"
          disabled={isLoading}
          onClick={() => setApprovalDialogCreatorId(_id)}
        >
          {loadingThisCreator === "pending-activation" ? (
            <Spinner />
          ) : (
            <Check />
          )}
        </Button>
        <ConfirmPopover
          title="Send Rejection Message?"
          action={
            <ConfirmPopoverLink asChild>
              <Link to={`/dashboard/campaigns/${campaignId}/brief/rejection_message`}>Edit</Link>
            </ConfirmPopoverLink>
          }
          description='Amy sends rejection message and moves creator to "Rejected" status.'
          confirmLabel={<><XIcon /> Reject Creator</>}
          confirmVariant="destructive"
          onConfirm={handleRejectCreator}
        >
          <Button
            size="icon"
            variant="destructive-ghost"
            className="shrink-0 size-7.5"
            disabled={isLoading}
          >
            {loadingThisCreator === "pending-rejection" ? (
              <Spinner />
            ) : (
              <XIcon />
            )}
          </Button>
        </ConfirmPopover>
      </div>
    </div>
  );
}
